import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../services/api';
import StatCard from '../components/StatCard';
import SimplePieChart from '../components/SimplePieChart';
import { BarChart3, Server, Link, Cpu, GitCompare, Upload } from 'lucide-react';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await dashboardAPI.getStats();
      
      // All data comes pre-processed from the backend
      setStats(response.data);
      
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось получить статистику');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div>Загрузка дашборда...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error">
        {error}
      </div>
    );
  }

  return (
    <div style={{ padding: '28px' }}>
      <div className="page-header">
        <div className="page-header-icon">
          <BarChart3 size={24} />
        </div>
        <div className="page-header-text">
          <h1>Дашборд</h1>
          <p>Обзор данных по сканированию безопасности</p>
        </div>
      </div>

      <div className="grid grid-4" style={{ marginBottom: '32px' }}>
        <StatCard
          title="Всего хостов"
          value={stats?.total_hosts}
          icon={Server}
          color="#3b82f6"
        />
        <StatCard
          title="Всего ссылок"
          value={stats?.total_links}
          icon={Link}
          color="#10b981"
        />
        <StatCard
          title="Уникальные технологии"
          value={stats?.unique_technologies}
          icon={Cpu}
          color="#f59e0b"
        />
        <StatCard
          title="Обнаружено изменений"
          value={stats?.diff_count}
          icon={GitCompare}
          color="#ef4444"
        />
      </div>

      <div className="grid grid-3">
        <div className="card">
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            marginBottom: '16px',
          }}>
            Топ технологий
          </h3>
          {stats?.top_technologies && stats.top_technologies.length > 0 ? (
            <SimplePieChart data={stats.top_technologies} size={160} />
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '160px',
              color: 'var(--text-secondary)',
              fontSize: '14px'
            }}>
              Нет данных по технологиям
            </div>
          )}
        </div>

        <div className="card">
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            marginBottom: '16px',
          }}>
            Распределение IP-адресов
          </h3>
          {stats?.top_ips && stats.top_ips.length > 0 ? (
            <SimplePieChart data={stats.top_ips} size={160} />
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '160px',
              color: 'var(--text-secondary)',
              fontSize: '14px'
            }}>
              Нет данных по IP
            </div>
          )}
        </div>

        <div className="card">
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            marginBottom: '16px',
          }}>
            Последние изменения
          </h3>
          {stats?.recent_changes && stats.recent_changes.length > 0 ? (
            <SimplePieChart data={stats.recent_changes} size={160} />
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '160px',
              color: 'var(--text-secondary)',
              fontSize: '14px'
            }}>
              Нет последних изменений
            </div>
          )}
        </div>
      </div>

      {/* Autodiscovery runs */}
      {stats?.autodiscovery_runs && stats.autodiscovery_runs.length > 0 && (
        <div className="card" style={{ marginTop: '8px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '12px' }}>
            Последние автодискавери
          </h3>
          <table className="table" style={{ boxShadow: 'none', border: '1px solid var(--border-color)' }}>
            <thead>
              <tr>
                <th>Дата</th>
                <th>Конфигурация</th>
                <th>Статус</th>
                <th>Хосты</th>
                <th>Локации</th>
              </tr>
            </thead>
            <tbody>
              {stats.autodiscovery_runs.map(r => (
                <tr key={r._id}>
                  <td style={{ fontSize: '13px' }}>{r.created_at ? new Date(r.created_at).toLocaleString() : '-'}</td>
                  <td style={{ fontSize: '13px' }}>
                    {r.def_name || '-'}
                    {r.domains?.length > 0 && (
                      <span style={{ color: 'var(--text-tertiary)', fontSize: '12px', marginLeft: '6px' }}>
                        ({r.domains.slice(0, 2).join(', ')}{r.domains.length > 2 ? '...' : ''})
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={`badge ${
                      r.status === 'completed' ? 'badge-success' :
                      r.status === 'running' ? 'badge-info' :
                      r.status === 'failed' ? 'badge-danger' : 'badge-neutral'
                    }`}>
                      {r.status === 'completed' ? 'завершён' :
                       r.status === 'running' ? 'выполняется' :
                       r.status === 'failed' ? 'ошибка' :
                       r.status === 'pending' ? 'в ожидании' : r.status}
                    </span>
                  </td>
                  <td style={{ fontSize: '13px' }}>{r.stats?.processed_hosts ?? '-'}</td>
                  <td style={{ fontSize: '13px' }}>{r.stats?.total_locations ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: '16px' }}>
        <div className="card">
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            marginBottom: '16px',
          }}>
            Быстрые действия
          </h3>
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/config')}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Upload size={16} />
              Загрузить новый конфиг
            </button>
            <button 
              className="btn btn-secondary"
              onClick={() => navigate('/hosts')}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Server size={16} />
              Открыть все хосты
            </button>
            <button 
              className="btn btn-secondary"
              onClick={() => navigate('/diff')}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <GitCompare size={16} />
              Проверить различия
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
